import parse from 'html-react-parser';
import React from 'react';
import clientLogger from 'src/utils/logger';

/**
 * Safely parses HTML string into React elements using html-react-parser
 * @param htmlString - The HTML string to parse
 * @returns React.ReactNode or null if invalid/empty
 */
export function parseHtmlToReact(htmlString: string | null | undefined): React.ReactNode {
  // Handle null, undefined, or empty strings
  if (!htmlString || typeof htmlString !== 'string' || !htmlString.trim()) {
    return null;
  }

  try {
    // Use html-react-parser to safely convert HTML to React elements
    return parse(htmlString);
  } catch (error) {
    // If parsing fails, return the original HTML string
    clientLogger.warn(
      'Failed to parse HTML content',
      error instanceof Error ? error : new Error(String(error)),
    );
    return htmlString;
  }
}

/**
 * Extracts text content from React nodes
 * @param node - The React node to extract text from
 * @returns Plain text string
 */
function extractTextFromReactNode(node: React.ReactNode): string {
  // Handle null, undefined, boolean
  if (node == null || typeof node === 'boolean') {
    return '';
  }

  // Handle strings and numbers
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  // Handle arrays of React nodes
  if (Array.isArray(node)) {
    return node.map(extractTextFromReactNode).join('');
  }

  // Handle React elements
  if (React.isValidElement(node)) {
    // Extract text from children
    if (node.props && node.props.children) {
      return extractTextFromReactNode(node.props.children);
    }
  }

  // Fallback for any other types
  return '';
}

/**
 * Converts HTML string to plain text for WebinarCard component
 * @param htmlString - The HTML string to convert to plain text
 * @returns Plain text string or empty string if invalid/empty
 */
export function parseHtmlToReactForCard(htmlString: string | null | undefined): string {
  // Handle null, undefined, or empty strings
  if (!htmlString || typeof htmlString !== 'string' || !htmlString.trim()) {
    return '';
  }

  try {
    // First, parse HTML to React elements using the existing safe function
    const reactElements = parseHtmlToReact(htmlString);

    // Then extract text content from React elements
    const textContent = extractTextFromReactNode(reactElements);

    // Clean up extra whitespace and line breaks
    return textContent.replace(/\s+/g, ' ').trim();
  } catch (error) {
    // If parsing fails, return the original HTML string
    clientLogger.warn(
      'Failed to parse HTML content for card',
      error instanceof Error ? error : new Error(String(error)),
    );
    return htmlString;
  }
}

/**
 * Checks if a description has meaningful content (not null, empty, or just whitespace)
 * @param description - The description to check
 * @returns boolean indicating if description has content
 */
export function hasDescriptionContent(description: string | null | undefined): boolean {
  return Boolean(description && description.trim());
}
